import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { searchAll, type SearchResult } from "@/lib/film";

export default function GlobalSearch({ onNavigate }: { onNavigate?: () => void }) {
  const [open, setOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const wrapRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    setResults(searchAll(keyword, 6));
  }, [keyword]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  function goToResult(path: string) {
    setOpen(false);
    setKeyword("");
    onNavigate?.();
    navigate(path);
  }

  function submitSearch() {
    if (!keyword.trim()) return;
    setOpen(false);
    onNavigate?.();
    navigate(`/news?q=${encodeURIComponent(keyword.trim())}`);
  }

  return (
    <div ref={wrapRef} className="relative">
      <div className="flex items-center gap-1 rounded-full border border-background-300/70 bg-background-50/90 px-3 py-1.5 focus-within:border-primary-400">
        <i className="ri-search-line text-foreground-500 w-4 h-4 flex items-center justify-center text-sm"></i>
        <input
          value={keyword}
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            setKeyword(e.target.value);
            setOpen(true);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") submitSearch();
          }}
          placeholder="搜索新闻 / 剧情 / 演员"
          className="w-40 lg:w-52 bg-transparent text-sm outline-none placeholder:text-foreground-400"
        />
      </div>

      {open && keyword.trim() && (
        <div className="absolute right-0 mt-2 w-80 max-w-[85vw] overflow-hidden rounded-xl border border-background-200 bg-background-50 shadow-lg animate-fade-in">
          {results.length > 0 ? (
            <ul className="max-h-96 overflow-y-auto py-1">
              {results.map((item) => (
                <li key={`${item.type}-${item.id}`}>
                  <button
                    type="button"
                    onClick={() => goToResult(item.path)}
                    className="flex w-full items-start gap-3 px-3 py-2 text-left transition-colors hover:bg-background-100"
                  >
                    <span className="h-12 w-12 shrink-0 overflow-hidden rounded-md bg-background-100">
                      <img src={item.image} alt={item.title} className="h-full w-full object-cover object-top" />
                    </span>
                    <span className="min-w-0">
                      <span className="mb-0.5 inline-block rounded-full bg-secondary-100 px-2 py-0.5 text-[11px] text-secondary-900">
                        {item.typeLabel}
                      </span>
                      <span className="block truncate text-sm font-medium text-foreground-900">{item.title}</span>
                      <span className="block truncate text-xs text-foreground-500">{item.summary}</span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <div className="px-3 py-6 text-center text-sm text-foreground-500">没有找到相关内容，换个关键词试试</div>
          )}
          <button
            type="button"
            onClick={submitSearch}
            className="flex w-full items-center justify-center gap-1 border-t border-background-200 py-2.5 text-sm text-primary-700 transition-colors hover:bg-primary-50"
          >
            查看全部搜索结果 <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
          </button>
        </div>
      )}
    </div>
  );
}